import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useApiClient } from '../../lib/apiClient';
import { applyPreferredLanguage } from '../../i18n';

export type PortalPatientContextOption = {
  patientId: string;
  relationship: string;
  canMessage: boolean;
  firstName: string;
  lastName: string;
  mrn: string | null;
  dob: string;
  status: string;
  provider: {
    id: string;
    name: string;
    title?: string | null;
  } | null;
};

export type PortalPatientProfile = {
  id: string;
  firstName: string;
  lastName: string;
  mrn: string | null;
  email: string | null;
  phone: string | null;
  dob: string;
  sex: string | null;
  status: string;
  preferredLanguage: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  provider: {
    id: string;
    name: string;
    title?: string | null;
  } | null;
};

type PortalContextListResponse = {
  data?: PortalPatientContextOption[];
};

type PortalProfileResponse = {
  data?: PortalPatientProfile;
};

type PortalPatientState = {
  contexts: PortalPatientContextOption[];
  activePatientId: string;
  activeContext: PortalPatientContextOption | null;
  profile: PortalPatientProfile | null;
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
  refreshNonce: number;
  setActivePatientId: (patientId: string) => void;
  refreshPortalData: () => Promise<void>;
  buildPortalPath: (path: string) => string;
};

const PortalPatientDataContext = createContext<PortalPatientState | null>(null);

function withPatientContext(path: string, patientId?: string) {
  if (!patientId) return path;
  const [basePath, rawQuery = ''] = path.split('?');
  const params = new URLSearchParams(rawQuery);
  params.set('patientId', patientId);
  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

export function PortalPatientDataProvider({ children }: { children: ReactNode }) {
  const { fetchJson } = useApiClient();
  const [contexts, setContexts] = useState<PortalPatientContextOption[]>([]);
  const [activePatientId, setActivePatientId] = useState('');
  const [profile, setProfile] = useState<PortalPatientProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const loadContexts = useCallback(async () => {
    const response = await fetchJson<PortalContextListResponse>('/portal/access/contexts');
    const rows = Array.isArray(response?.data) ? response.data : [];
    setContexts(rows);
    setActivePatientId((current) => {
      if (current && rows.some((item) => item.patientId === current)) return current;
      return rows[0]?.patientId || '';
    });
    return rows;
  }, [fetchJson]);

  const loadProfile = useCallback(
    async (patientId: string) => {
      if (!patientId) {
        setProfile(null);
        setProfileLoading(false);
        return null;
      }
      const response = await fetchJson<PortalProfileResponse>(withPatientContext('/portal/me', patientId));
      const nextProfile = response?.data || null;
      setProfile(nextProfile);
      // Detect initial language from the authenticated patient's preferredLanguage
      // (LATAM-first; only applied when the user has not made an explicit choice).
      applyPreferredLanguage(nextProfile?.preferredLanguage);
      return nextProfile;
    },
    [fetchJson],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const rows = await loadContexts();
        const nextPatientId = rows[0]?.patientId || '';
        if (!active) return;
        if (!nextPatientId) {
          setProfile(null);
          setProfileLoading(false);
          return;
        }
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || 'Failed to load patient portal access.');
        setProfile(null);
        setProfileLoading(false);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [loadContexts]);

  useEffect(() => {
    let active = true;
    setProfileLoading(true);

    (async () => {
      try {
        const nextProfile = await loadProfile(activePatientId);
        if (!active) return;
        setProfile(nextProfile);
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || 'Failed to load patient profile.');
        setProfile(null);
      } finally {
        if (active) setProfileLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [activePatientId, loadProfile]);

  const refreshPortalData = useCallback(async () => {
    setError(null);
    const rows = await loadContexts();
    const nextPatientId = rows.some((item) => item.patientId === activePatientId)
      ? activePatientId
      : rows[0]?.patientId || '';
    await loadProfile(nextPatientId);
    setRefreshNonce((value) => value + 1);
  }, [activePatientId, loadContexts, loadProfile]);

  const activeContext = useMemo(
    () => contexts.find((item) => item.patientId === activePatientId) || contexts[0] || null,
    [activePatientId, contexts],
  );

  const value = useMemo<PortalPatientState>(
    () => ({
      contexts,
      activePatientId,
      activeContext,
      profile,
      loading,
      profileLoading,
      error,
      refreshNonce,
      setActivePatientId,
      refreshPortalData,
      buildPortalPath: (path: string) => withPatientContext(path, activePatientId || activeContext?.patientId),
    }),
    [
      activeContext,
      activePatientId,
      contexts,
      error,
      loading,
      profile,
      profileLoading,
      refreshNonce,
      refreshPortalData,
    ],
  );

  return <PortalPatientDataContext.Provider value={value}>{children}</PortalPatientDataContext.Provider>;
}

export function usePortalPatientData() {
  const value = useContext(PortalPatientDataContext);
  if (!value) {
    throw new Error('usePortalPatientData must be used within PortalPatientDataProvider.');
  }
  return value;
}
